import React, { useState } from 'react'
import { Plus, X, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import type { GalleryImage } from '@/types/projectPages'

interface GalleryImagesManagerProps {
  value: GalleryImage[]
  onChange: (images: GalleryImage[]) => void
}

/**
 * Gallery Images Manager Component
 *
 * Manages gallery images with visual thumbnails, allowing:
 * - Adding new images with URL, alt text, and caption
 * - Editing existing images
 * - Removing images
 * - Reordering images (via array manipulation)
 */
export default function GalleryImagesManager({ value = [], onChange }: GalleryImagesManagerProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<GalleryImage>({
    url: '',
    alt: '',
    caption: ''
  })
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

  const handleAdd = () => {
    if (!editForm.url || !editForm.alt) return

    onChange([...value, { ...editForm }])
    setEditForm({ url: '', alt: '', caption: '' })
    setIsAddDialogOpen(false)
  }

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setEditForm({ ...value[index] })
  }

  const handleUpdate = () => {
    if (editingIndex === null || !editForm.url || !editForm.alt) return

    const updated = [...value]
    updated[editingIndex] = { ...editForm }
    onChange(updated)
    setEditingIndex(null)
    setEditForm({ url: '', alt: '', caption: '' })
  }

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const updated = [...value]
    ;[updated[index - 1], updated[index]] = [updated[index], updated[index - 1]]
    onChange(updated)
  }

  const handleMoveDown = (index: number) => {
    if (index === value.length - 1) return
    const updated = [...value]
    ;[updated[index], updated[index + 1]] = [updated[index + 1], updated[index]]
    onChange(updated)
  }

  return (
    <div className="space-y-4">
      {/* Image Grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {value.map((image, index) => (
            <Card key={index} className="overflow-hidden group relative">
              <CardContent className="p-0">
                {/* Image Thumbnail */}
                <div className="relative aspect-square bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/400x400?text=Image+Not+Found'
                    }}
                  />

                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(index)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemove(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Reorder Controls */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveUp(index)}
                      >
                        ↑
                      </Button>
                    )}
                    {index < value.length - 1 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveDown(index)}
                      >
                        ↓
                      </Button>
                    )}
                  </div>

                  {/* Index Badge */}
                  <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    #{index + 1}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-2 bg-white">
                  <p className="text-xs text-gray-600 line-clamp-1" title={image.alt}>
                    {image.alt}
                  </p>
                  {image.caption && (
                    <p className="text-xs text-gray-500 line-clamp-1" title={image.caption}>
                      {image.caption}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Image Button */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="w-full">
            <Plus className="mr-2 h-4 w-4" />
            Add Gallery Image
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Gallery Image</DialogTitle>
            <DialogDescription>
              Add a new image to the project gallery
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-image-url">Image URL *</Label>
              <Input
                id="new-image-url"
                value={editForm.url}
                onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                placeholder="https://..."
                type="url"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-image-alt">Alt Text *</Label>
              <Input
                id="new-image-alt"
                value={editForm.alt}
                onChange={(e) => setEditForm({ ...editForm, alt: e.target.value })}
                placeholder="Describe the image for accessibility"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-image-caption">Caption (optional)</Label>
              <Input
                id="new-image-caption"
                value={editForm.caption || ''}
                onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                placeholder="Optional caption"
              />
            </div>

            {/* Preview */}
            {editForm.url && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="relative aspect-video bg-gray-100 rounded overflow-hidden">
                  <img
                    src={editForm.url}
                    alt={editForm.alt || 'Preview'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Invalid+URL'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={!editForm.url || !editForm.alt}
            >
              Add Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Image Dialog */}
      {editingIndex !== null && (
        <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Gallery Image</DialogTitle>
              <DialogDescription>
                Update the image details
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-image-url">Image URL *</Label>
                <Input
                  id="edit-image-url"
                  value={editForm.url}
                  onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                  placeholder="https://..."
                  type="url"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-image-alt">Alt Text *</Label>
                <Input
                  id="edit-image-alt"
                  value={editForm.alt}
                  onChange={(e) => setEditForm({ ...editForm, alt: e.target.value })}
                  placeholder="Describe the image for accessibility"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-image-caption">Caption (optional)</Label>
                <Input
                  id="edit-image-caption"
                  value={editForm.caption || ''}
                  onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                  placeholder="Optional caption"
                />
              </div>

              {/* Preview */}
              {editForm.url && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="relative aspect-video bg-gray-100 rounded overflow-hidden">
                    <img
                      src={editForm.url}
                      alt={editForm.alt || 'Preview'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Invalid+URL'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingIndex(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpdate}
                disabled={!editForm.url || !editForm.alt}
              >
                Update Image
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
